import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import Badge from '@mui/material/Badge'
import IconButton from '@mui/material/IconButton'
import Popover from '@mui/material/Popover'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import List from '@mui/material/List'
import Divider from '@mui/material/Divider'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Tooltip from '@mui/material/Tooltip'

import NotificationsIcon from '@mui/icons-material/Notifications'
import SettingsIcon from '@mui/icons-material/Settings'
import DoneAllIcon from '@mui/icons-material/DoneAll'

import {
    useGetNotificationsQuery,
    useGetUnreadNotificationsCountQuery,
    useMarkNotificationReadMutation,
    useMarkAllNotificationsReadMutation
} from '@/store/api/notificationsApi'
import NotificationListItem from './NotificationListItem'

const NotificationBell = ({ onOpenPreferences }) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [anchorEl, setAnchorEl] = useState(null)
    const open = Boolean(anchorEl)

    const { data: unreadData } = useGetUnreadNotificationsCountQuery(undefined, {
        pollingInterval: 30_000
    })
    const { data, isFetching } = useGetNotificationsQuery({ limit: 20 }, { skip: !open })
    const [markRead] = useMarkNotificationReadMutation()
    const [markAllRead] = useMarkAllNotificationsReadMutation()

    const unreadCount = unreadData?.count || 0
    const items = data?.items || []

    const handleItemClick = (notification) => {
        if (!notification.readAt) markRead(notification.tid)
    }

    return (
        <>
            <IconButton
                onClick={(e) => setAnchorEl(e.currentTarget)}
                size='small'
                aria-label={t('Notifications')}>
                <Badge badgeContent={unreadCount} color='error' max={99}>
                    <NotificationsIcon fontSize='small' />
                </Badge>
            </IconButton>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                disableScrollLock
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                slotProps={{ paper: { sx: { width: 380, maxHeight: 480 } } }}>
                <Stack
                    direction='row'
                    sx={{ alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
                    <Typography variant='subtitle1' fontWeight={600}>
                        {t('Notifications')}
                    </Typography>
                    <Stack direction='row' spacing={0.5}>
                        {unreadCount > 0 && (
                            <Tooltip title={t('Mark all as read')}>
                                <IconButton size='small' onClick={() => markAllRead()}>
                                    <DoneAllIcon fontSize='small' />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title={t('Notification settings')}>
                            <IconButton
                                size='small'
                                onClick={() => {
                                    setAnchorEl(null)
                                    onOpenPreferences?.()
                                }}>
                                <SettingsIcon fontSize='small' />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
                <Divider />

                {isFetching ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : items.length === 0 ? (
                    <Box sx={{ py: 4, px: 2, textAlign: 'center' }}>
                        <Typography variant='body2' color='text.secondary'>
                            {t('No notifications yet')}
                        </Typography>
                    </Box>
                ) : (
                    <List dense disablePadding sx={{ maxHeight: 380, overflowY: 'auto' }}>
                        {items.map((notification) => (
                            <NotificationListItem
                                key={notification.tid}
                                notification={notification}
                                onClick={() => handleItemClick(notification)}
                            />
                        ))}
                    </List>
                )}
                <Divider />
                <Button
                    fullWidth
                    size='small'
                    onClick={() => {
                        setAnchorEl(null)
                        navigate('/notifications')
                    }}
                    sx={{ borderRadius: 0, py: 1 }}>
                    {t('See all notifications')}
                </Button>
            </Popover>
        </>
    )
}

export default NotificationBell
